import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { AppUser } from '../../types/user';

type FirestoreTimestampLike = {
  toDate: () => Date;
};

type UserRow = {
  uid: string;
  email: string;
  displayName?: string;
  role: AppUser['role'];
  status?: AppUser['status'];
  lastLogin?: unknown;
  lastActiveAt?: unknown;
  programmationsCount: number;
};

type SortKey = 'email' | 'displayName' | 'role' | 'programmationsCount' | 'lastLogin' | 'lastActiveAt';
type SortDirection = 'asc' | 'desc';

function toDateOrNull(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const v = value as FirestoreTimestampLike;
    try {
      return v.toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function formatDateTimeFr(value: unknown): string {
  const d = toDateOrNull(value);
  if (!d) return '-';
  return d.toLocaleString('fr-FR');
}

function diffDaysFromNow(value: unknown): number | null {
  const d = toDateOrNull(value);
  if (!d) return null;
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

const AdminStats = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [programmationsCountByUserId, setProgrammationsCountByUserId] = useState<Record<string, number>>({});
  const [sortKey, setSortKey] = useState<SortKey>('lastLogin');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [usersSnap, programmationsSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'programmations')),
        ]);

        const usersList = usersSnap.docs.map((d) => d.data() as AppUser);

        const counts: Record<string, number> = {};
        programmationsSnap.docs.forEach((d) => {
          const data = d.data() as { userId?: unknown };
          const userId = typeof data.userId === 'string' ? data.userId : null;
          if (!userId) return;
          counts[userId] = (counts[userId] ?? 0) + 1;
        });

        setUsers(usersList);
        setProgrammationsCountByUserId(counts);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Erreur inconnue';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const rows = useMemo<UserRow[]>(() => {
    const mapped = users.map((u) => ({
      uid: u.uid,
      email: u.email,
      displayName: u.displayName,
      role: u.role,
      status: u.status,
      lastLogin: u.lastLogin,
      lastActiveAt: u.lastActiveAt,
      programmationsCount: programmationsCountByUserId[u.uid] ?? 0,
    }));

    const getComparable = (row: UserRow, key: SortKey): string | number => {
      if (key === 'programmationsCount') return row.programmationsCount;
      if (key === 'lastLogin' || key === 'lastActiveAt') {
        return toDateOrNull(row[key])?.getTime() ?? 0;
      }
      const v = row[key];
      return (typeof v === 'string' ? v : '').toLocaleLowerCase();
    };

    const directionFactor = sortDirection === 'asc' ? 1 : -1;

    return mapped.sort((a, b) => {
      const av = getComparable(a, sortKey);
      const bv = getComparable(b, sortKey);
      if (av < bv) return -1 * directionFactor;
      if (av > bv) return 1 * directionFactor;
      return 0;
    });
  }, [programmationsCountByUserId, sortDirection, sortKey, users]);

  const handleRequestSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const totalUsers = rows.length;
  const totalProgrammations = useMemo(() => {
    return Object.values(programmationsCountByUserId).reduce((acc, v) => acc + v, 0);
  }, [programmationsCountByUserId]);

  const active7d = useMemo(() => {
    return rows.filter((r) => {
      const d = diffDaysFromNow(r.lastActiveAt ?? r.lastLogin);
      return d !== null && d <= 7;
    }).length;
  }, [rows]);

  const active30d = useMemo(() => {
    return rows.filter((r) => {
      const d = diffDaysFromNow(r.lastActiveAt ?? r.lastLogin);
      return d !== null && d <= 30;
    }).length;
  }, [rows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Utilisateurs
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {totalUsers}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Programmations (total)
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {totalProgrammations}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Actifs (7 jours)
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {active7d}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Actifs (30 jours)
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {active30d}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Divider />

      <Typography variant="h6" fontWeight={800}>
        Utilisation par utilisateur
      </Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small" aria-label="table statistiques admin">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortKey === 'email' ? sortDirection : false}>
                <TableSortLabel
                  active={sortKey === 'email'}
                  direction={sortKey === 'email' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'displayName' ? sortDirection : false}>
                <TableSortLabel
                  active={sortKey === 'displayName'}
                  direction={sortKey === 'displayName' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('displayName')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'role' ? sortDirection : false}>
                <TableSortLabel
                  active={sortKey === 'role'}
                  direction={sortKey === 'role' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Rôle
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'programmationsCount' ? sortDirection : false}>
                <TableSortLabel
                  active={sortKey === 'programmationsCount'}
                  direction={sortKey === 'programmationsCount' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('programmationsCount')}
                >
                  Programmations
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'lastLogin' ? sortDirection : false}>
                <TableSortLabel
                  active={sortKey === 'lastLogin'}
                  direction={sortKey === 'lastLogin' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('lastLogin')}
                >
                  Dernière connexion
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortKey === 'lastActiveAt' ? sortDirection : false}>
                <TableSortLabel
                  active={sortKey === 'lastActiveAt'}
                  direction={sortKey === 'lastActiveAt' ? sortDirection : 'asc'}
                  onClick={() => handleRequestSort('lastActiveAt')}
                >
                  Dernière activité
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.uid} hover>
                <TableCell>{r.email}</TableCell>
                <TableCell>{r.displayName || '-'}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{r.role}</TableCell>
                <TableCell>{r.programmationsCount}</TableCell>
                <TableCell>{formatDateTimeFr(r.lastLogin)}</TableCell>
                <TableCell>{formatDateTimeFr(r.lastActiveAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary">
        Notes: les champs « dernière connexion » et « dernière activité » sont alimentés lors des connexions (et à chaque changement d’état Auth). Les anciens utilisateurs peuvent afficher « - » tant qu’ils ne se reconnectent pas.
      </Typography>
    </Box>
  );
};

export default AdminStats;
